import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { settingsService } from 'services/firestore';
import useSiteNav from 'hooks/useSiteNav';
import { resolveNavLink, hrefOf } from 'services/resolveNavLink';
import { formatBusinessHours } from './formatBusinessHours';
import 'styles/components/footer.css';

// 60-second in-memory cache so route changes don't re-fetch the store doc.
// `getStoreSettingsCached` is the single read point — Footer + any future
// consumer (contact page, etc.) shares the same cached promise.
let _storeCache = null;
let _storeFetchedAt = 0;
const STORE_TTL_MS = 60 * 1000;

const getStoreSettingsCached = async () => {
  const now = Date.now();
  if (_storeCache && (now - _storeFetchedAt) < STORE_TTL_MS) return _storeCache;
  try {
    const data = await settingsService.get('store');
    _storeCache = data;
    _storeFetchedAt = now;
    return data;
  } catch {
    // On error, return whatever's cached (may be null) so the footer still
    // renders with fallbacks instead of crashing.
    return _storeCache;
  }
};

// Fallbacks — used when settings/store doesn't exist or hasn't loaded yet.
// These match what was previously hardcoded so first paint looks identical.
const FALLBACK = {
  instagram: 'curatedlagos',
  // tiktok is not yet operator-editable; v1 keeps it constant.
  tiktok: 'curatedlagos',
};

const Footer = () => {
  const location = useLocation();
  const [store, setStore] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getStoreSettingsCached().then((data) => {
      if (!cancelled) setStore(data);
    });
    return () => { cancelled = true; };
  }, []);

  // Site CMS: footer columns from `/site_settings/nav.footer` via the
  // 60s `useSiteNav` cache (STORY-13.4.05). DEFAULT_NAV renders
  // synchronously, so first paint is unchanged. Each item flows
  // through resolveNavLink to get its final href.
  const { nav } = useSiteNav();
  const footerColumns = useMemo(() => {
    const cols = Array.isArray(nav?.footer) ? nav.footer : [];
    // These affordances are now icons in the brand column above, so any
    // CMS-driven column items with these labels are filtered out to avoid
    // the duplicate. When a column (e.g. "Connect") becomes empty after
    // filtering, the empty-column guard below skips rendering it entirely.
    const ICON_DUPLICATES = new Set([
      'instagram', 'tiktok', 'tik tok', 'email', 'newsletter',
    ]);
    return cols.map((col, colIndex) => ({
      key: `col-${colIndex}`,
      heading: typeof col?.heading === 'string' ? col.heading : '',
      items: (Array.isArray(col?.items) ? col.items : [])
        .filter((item) => typeof item?.label !== 'string'
          || !ICON_DUPLICATES.has(item.label.trim().toLowerCase()))
        .map((item, itemIndex) => {
          const resolved = resolveNavLink(item);
          return {
            key: `${item.label || 'item'}-${itemIndex}`,
            label: item.label,
            href: hrefOf(resolved),
            openNewTab: item.open_new_tab === true,
            isAction: typeof resolved === 'object' && resolved?.kind === 'action',
          };
        }),
    }));
  }, [nav]);

  // Hide footer on checkout (e-commerce convention)
  if (location.pathname === '/checkout') {
    return null;
  }

  const instagramHandle = (store?.instagram || FALLBACK.instagram).replace(/^@+/, '');
  const instagramUrl = `https://www.instagram.com/${instagramHandle}/`;
  const tiktokUrl = `https://www.tiktok.com/@${FALLBACK.tiktok}/`;

  // Optional contact-info block — email moved to the social icon row above
  // (no duplicate), so this only renders when phone is set. WhatsApp drives
  // the floating chat button, not a footer link, so it must NOT gate this
  // block. Code-review W1, AI-CODE-REVIEW-FEAT-03.1-2026-05-26.
  const hasContact = Boolean(store?.phone);

  // Business hours, collapsed into day-ranges (STORY-03.1.03 AC-5). Empty when
  // settings/store has no hours yet — the block is simply omitted.
  const hours = formatBusinessHours(store?.hours);

  return (
    <footer className="footer" role="contentinfo">
      <div className="footer__container">
        <div className="footer__grid">
          {/* Brand column */}
          <div className="footer__brand">
            <div className="footer__wordmark">
              <span className="footer__wordmark-curated">curated</span>
              <span className="footer__wordmark-lagos">LAGOS</span>
            </div>
            <p className="footer__tagline">
              Premium gift boxes of authentic, made-in-Nigeria goods — delivered
              with intention.
            </p>
            <div className="footer__social">
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="footer__social-link"
                aria-label="Follow us on Instagram"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                </svg>
              </a>
              <a
                href={tiktokUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="footer__social-link"
                aria-label="Follow us on TikTok"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M19 8.5a6 6 0 0 1-4-1.5v7.5a5 5 0 1 1-5-5v2.5a2.5 2.5 0 1 0 2.5 2.5V3h2.5a3.5 3.5 0 0 0 4 3.5v2Z" />
                </svg>
              </a>
              {store?.email && (
                <a
                  href={`mailto:${store.email}`}
                  className="footer__social-link"
                  aria-label={store.email}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="14" rx="1" />
                    <path d="M3 7l9 6 9-6" />
                  </svg>
                </a>
              )}
              <a
                href="/#newsletter"
                className="footer__social-link"
                aria-label="Subscribe to the newsletter"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M22 2 11 13" />
                  <path d="M22 2 15 22 11 13 2 9z" />
                </svg>
              </a>
            </div>

            {hasContact && (
              <ul className="footer__contact">
                {store.phone && (
                  <li><a href={`tel:${store.phone.replace(/\s/g, '')}`}>{store.phone}</a></li>
                )}
              </ul>
            )}

            {hours.length > 0 && (
              <div className="footer__hours">
                <h6 className="footer__hours-heading">Hours</h6>
                <ul className="footer__hours-list">
                  {hours.map((row) => (
                    <li key={row.label} className="footer__hours-row">
                      <span className="footer__hours-days">{row.label}</span>
                      <span className="footer__hours-value">{row.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* 4-column footer rendered from `/site_settings/nav.footer`
           * (STORY-13.4.05). Every field — heading, label, href, target
           * type — is operator-editable via the Site CMS Navigation tab.
           * `open_new_tab` items render as `<a target="_blank">`;
           * everything else uses `<Link>` for internal routing. */}
          {footerColumns.map((col) => {
            if (col.items.length === 0) return null;
            return (
              <div className="footer__column" key={col.key}>
                {col.heading && (
                  <h6 className="footer__heading">{col.heading}</h6>
                )}
                <ul className="footer__links">
                  {col.items.map((l) => (
                    <li key={l.key}>
                      {l.openNewTab || l.isAction ? (
                        <a
                          href={l.href}
                          target={l.openNewTab ? '_blank' : undefined}
                          rel={l.openNewTab ? 'noopener noreferrer' : undefined}
                        >
                          {l.label}
                        </a>
                      ) : (
                        <Link to={l.href}>{l.label}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Bottom bar */}
        <div className="footer__bottom">
          <span>&copy; {new Date().getFullYear()} {store?.name || 'Curated Lagos'}. All rights reserved.</span>
          <span className="footer__bottom-mid">Made in Nigeria &middot; Given with intention</span>
          <span className="footer__bottom-right footer__credit">
            By{' '}
            <a
              href="https://www.dataxyzconnect.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
            >
              DATAXYZ Ltd
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
